import {
  createInstance,
  type Callback,
  type i18n as I18nInstance,
  use as i18nUse,
} from 'i18next';
import { initReactI18next } from 'react-i18next';

import { LanguageUtils, SupportedLanguage } from '@foundation/localization';

export type ResourceLoader = (
  language: string,
  namespace: string,
) => Promise<Record<string, unknown>>;

export async function createI18nextInstance(config: {
  readonly namespaces: ReadonlyArray<string>;
  readonly resourceLoader: ResourceLoader;
  readonly lazyLoadResources?: boolean;
  readonly debug?: boolean;
}) {
  const {
    namespaces,
    resourceLoader,
    lazyLoadResources = false,
    debug = false,
  } = config;

  // Load all resources dynamically
  const resources: Record<string, Record<string, Record<string, unknown>>> = {};
  const languages = Object.values(SupportedLanguage);

  // Pre-load resources based on lazy loading configuration
  const languagesToPreLoad = lazyLoadResources
    ? [SupportedLanguage.English] // Only preload English if lazy loading is enabled
    : languages; // Preload all languages if lazy loading is disabled

  for (const language of languagesToPreLoad) {
    for (const namespace of namespaces) {
      try {
        resources[language] ??= {};
        resources[language][namespace] = await resourceLoader(
          language,
          namespace,
        );
      } catch (error) {
        console.warn(
          `Failed to load translations for ${language}/${namespace}:`,
          error,
        );
      }
    }
  }

  const i18nInstance = i18nUse(initReactI18next).createInstance({
    /**
     * i18next options
     */
    debug: debug,
    ns: namespaces,
    defaultNS: namespaces[0],
    resources: resources,
    fallbackLng: SupportedLanguage.English,
    lng: SupportedLanguage.English,
    supportedLngs: false, // Disable strict checking since all resources are pre-loaded
    keySeparator: false,
    nsSeparator: false,
    load: 'currentOnly',

    /**
     * react-i18next options
     */
    react: {
      bindI18n: 'languageChanged',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['h1', 'h2', 'p', 'ul', 'li', 'strong'],
    },
  });

  const originalChangeLanguage = i18nInstance.changeLanguage;

  // Override changeLanguage to load resources first
  i18nInstance.changeLanguage = async (
    language?: string,
    callback?: Callback,
  ) => {
    const lng =
      LanguageUtils.language(language ?? '') ?? SupportedLanguage.English;

    // Load all namespaces in parallel and wait for all to complete
    await Promise.all(
      namespaces.map(async (ns) => {
        if (i18nInstance.hasResourceBundle(lng, ns)) {
          return;
        }

        try {
          const translations = await resourceLoader(lng, ns);
          i18nInstance.addResourceBundle(lng, ns, translations, true, true);
        } catch (error) {
          console.warn(`Failed to load translations for ${lng}/${ns}:`, error);
          i18nInstance.addResourceBundle(lng, ns, {}, true, true);
        }
      }),
    );

    return originalChangeLanguage(lng, callback);
  };

  await i18nInstance.init();

  return i18nInstance;
}

/**
 * Sync variant of {@link createI18nextInstance}, intended for Next.js pages
 * router SSR where the render tree cannot await async initialization and the
 * translations for the requested language have already been fetched (typically
 * in `getServerSideProps`) and handed off via `pageProps`.
 *
 * A fresh instance should be created per SSR request to avoid concurrent
 * requests overwriting each other's language on a shared server-side singleton.
 */
export function createI18nextInstanceSync(config: {
  readonly namespace: string;
  readonly language?: string;
  readonly resources?: Record<string, unknown>;
  readonly debug?: boolean;
}): I18nInstance {
  const { language, namespace, resources, debug = false } = config;

  const normalizedLanguage =
    LanguageUtils.language(language) ?? SupportedLanguage.English;

  const instance = createInstance();
  instance.use(initReactI18next).init({
    debug,
    lng: normalizedLanguage,
    fallbackLng: SupportedLanguage.English,
    ns: [namespace],
    defaultNS: namespace,
    resources: resources
      ? { [normalizedLanguage]: { [namespace]: resources } }
      : {},
    supportedLngs: false,
    keySeparator: false,
    nsSeparator: false,
    load: 'currentOnly',
    interpolation: { escapeValue: false },
    react: {
      bindI18n: 'languageChanged',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['h1', 'h2', 'p', 'ul', 'li', 'strong'],
    },
  });

  return instance;
}
